import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  setDoc,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { useNavigate } from "react-router-dom";

const AdminCustomOrders = () => {
  const [orders, setOrders] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [partnerInputs, setPartnerInputs] = useState({});
  const [confirmOrderId, setConfirmOrderId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const orderQuery = query(collection(db, "custom_orders"), where("status", "==", "pending"));
        const orderSnap = await getDocs(orderQuery);
        const ordersData = orderSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        const partnerQuery = query(collection(db, "users"), where("role", "==", "deliveryPartner"));
        const partnerSnap = await getDocs(partnerQuery);
        const partnersData = partnerSnap.docs.map((doc) => ({ uid: doc.id, ...doc.data() }));

        setOrders(ordersData);
        setPartners(partnersData);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data. Check permissions.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const assignToPartner = async (order, partnerId) => {
    const deliveryId = `${order.userId}_${Date.now()}`;
    await setDoc(doc(db, "deliveries", deliveryId), {
      date: new Date().toISOString().slice(0, 10),
      userId: order.userId,
      customOrderId: order.id,
      assignedPartnerId: partnerId,
      status: "pending",
      slot: order.slot || "Morning",
      items: order.items || [{ name: `Rotis x${order.qty}` }],
      address: order.address || "N/A",
      area: order.area || "N/A",
      createdAt: new Date(),
    });
    await updateDoc(doc(db, "custom_orders", order.id), {
      status: "assigned",
    });
    alert("✅ Order assigned");
    setPartnerInputs((prev) => ({ ...prev, [order.id]: "" }));
    setConfirmOrderId(null);
  };

  if (loading) return <div className="p-4">🔄 Loading custom orders...</div>;
  if (error) return <div className="p-4 text-red-500">❌ {error}</div>;

  return (
    <div className="p-6">
      <button
        className="mb-4 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
        onClick={() => navigate('/admin/dashboard')}
      >
        ← Back to Dashboard
      </button>
      <h2 className="text-2xl font-bold text-green-700 mb-4">📩 Pending Custom Orders</h2>
      {orders.length === 0 ? (
        <p className="text-gray-500">No custom orders found.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {orders.map((order) => (
            <div key={order.id} className="border border-green-200 rounded-xl p-4 shadow-sm bg-white">
              <h2 className="text-lg font-semibold text-green-800 mb-2">
                👤 {order.userName || order.userId || "Unnamed Customer"}
              </h2>
              <p className="text-sm text-gray-500 mb-1">📅 Date: {order.date || "N/A"}</p>
              <p className="text-sm text-gray-500 mb-2">📍 Address: {order.address || "N/A"}</p>

              <div className="mb-2">
                <strong className="text-gray-700">🍽 Items:</strong>
                <ul className="list-disc ml-5 text-sm text-gray-700 mt-1">
                  {Array.isArray(order.items) ? (
                    order.items.map((item, idx) => (
                      <li key={idx}>{item.name} — ₹{item.price}</li>
                    ))
                  ) : (
                    <li className="italic text-gray-400">No items listed</li>
                  )}
                </ul>
              </div>

              <p className="text-sm text-gray-600 mt-2">
                🧾 Status: <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                  order.status === "pending" ? "bg-yellow-100 text-yellow-800" :
                  order.status === "assigned" ? "bg-blue-100 text-blue-800" :
                  "bg-green-100 text-green-800"
                }`}>{order.status || "pending"}</span>
              </p>

              <div className="mt-4">
                <select
                  className="border px-2 py-1 rounded w-full mb-2"
                  value={partnerInputs[order.id] || ""}
                  onChange={(e) => setPartnerInputs((prev) => ({ ...prev, [order.id]: e.target.value }))}
                >
                  <option value="">-- Select Delivery Partner --</option>
                  {partners.map((partner) => (
                    <option key={partner.uid} value={partner.uid}>
                      {partner.name || partner.uid}
                    </option>
                  ))}
                </select>
                <button
                  className="bg-blue-600 text-white px-3 py-1 rounded w-full hover:bg-blue-700"
                  onClick={() => setConfirmOrderId(order.id)}
                >
                  Assign to Partner
                </button>
              </div>

              {/* Confirmation Modal */}
              {confirmOrderId === order.id && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white p-6 rounded shadow-xl w-full max-w-sm">
                    <h3 className="text-lg font-semibold mb-4">Confirm Assignment</h3>
                    <p className="text-sm text-gray-700 mb-2">
                      Are you sure you want to assign this order to:
                    </p>
                    <p className="text-green-700 font-semibold mb-4">
                      {partners.find((p) => p.uid === partnerInputs[order.id])?.name || partnerInputs[order.id]}
                    </p>
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => setConfirmOrderId(null)}
                        className="px-3 py-1 bg-gray-300 rounded"
                      >Cancel</button>
                      <button
                        onClick={() => assignToPartner(order, partnerInputs[order.id])}
                        className="px-3 py-1 bg-blue-600 text-white rounded"
                      >Confirm</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCustomOrders;
